/**
 * Index des comptes clients — lu depuis la vue canonique `v_account_role` (base).
 *
 * `v_account_role` dérive le rôle du REVENU réel (abonnement Stripe actif OU facture
 * d'abonnement liée au lead), pas de `crm_accounts.status` (pollué : RTA / transdev.com /
 * doublons marqués `customer` à tort). C'est LA source unique de « qui est client » —
 * même définition que `src/utils/revenue.ts`, matérialisée en base pour qu'aucune surface
 * ni aucun import ne puisse la re-dériver de travers. Cf. PLAN-ACCOUNT-MODEL Phase 2.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { logWarn } from './logger';
import { supabase } from './supabase';
import { cadenceFromCategory, type ClientBadge } from '../utils/clientStatus';

const db = supabase as unknown as SupabaseClient;

interface AccountRoleRow {
  account_id: string;
  role: 'client' | 'former_client' | 'prospect';
  source: 'stripe' | 'invoice' | null;
  invoice_cadence: string | null;
}

/** Map `account_id → ClientBadge` pour les comptes client / ancien client. */
export async function fetchClientAccountIndex(): Promise<Map<string, ClientBadge>> {
  // PostgREST plafonne toute réponse à max_rows=1000 SANS erreur : demander plus
  // est un mensonge. ~60 comptes client/ancien client aujourd'hui — si on
  // approche 1000 un jour, paginer (.range) ou passer par une RPC agrégée.
  const { data } = await db.from('v_account_role')
    .select('account_id, role, source, invoice_cadence')
    .in('role', ['client', 'former_client'])
    .limit(1000);

  const index = new Map<string, ClientBadge>();
  for (const r of (data ?? []) as AccountRoleRow[]) index.set(r.account_id, badgeOf(r));
  return index;
}

/** One row of `v_account_role` as the badge the screens show. */
function badgeOf(r: AccountRoleRow): ClientBadge {
  if (r.role === 'former_client') return { kind: 'former' };
  if (r.role === 'prospect') return { kind: 'prospect' };
  if (r.source === 'stripe') return { kind: 'client', source: 'stripe' };
  return { kind: 'client', source: 'invoice', cadence: cadenceFromCategory(r.invoice_cadence) };
}

/**
 * The badge of ONE account (its working sheet), from the same view: a prospect when it has no row;
 * null when the view cannot be read — a client must never show as « Prospect » because of an error.
 */
export async function fetchAccountBadge(accountId: string): Promise<ClientBadge | null> {
  const { data, error } = await db.from('v_account_role')
    .select('account_id, role, source, invoice_cadence')
    .eq('account_id', accountId)
    .maybeSingle();
  if (error) {
    logWarn('crm', 'account role unreadable', { accountId, error: error.message });
    return null;
  }
  return data ? badgeOf(data as AccountRoleRow) : { kind: 'prospect' };
}
